import type { WaveParams } from "./types";

/**
 * Options for {@link generateWaveSpectrum}. The generator sums many small waves
 * with irrational wavelength ratios, spread directions, and realistic deep-water
 * dispersion (longer waves travel faster) so the surface looks organic and does
 * not visibly tile.
 */
export interface SpectrumOptions {
  /** Number of wave components. More = richer, but costs more per pixel. */
  count?: number;
  /** Longest wavelength in the set, in world units. */
  maxWavelength?: number;
  /** Shortest wavelength in the set, in world units. */
  minWavelength?: number;
  /** Dominant propagation direction, in radians. */
  direction?: number;
  /** Angular spread around the dominant direction, in radians. */
  spread?: number;
  /** Overall amplitude scale (roughly the tallest component). */
  amplitude?: number;
  /** How fast amplitude falls off toward shorter wavelengths. Higher = calmer chop. */
  falloff?: number;
  /** Global phase-speed multiplier. */
  speed?: number;
  /** Seed for deterministic generation. */
  seed?: number;
}

/** Small deterministic PRNG (mulberry32) so presets are stable across loads. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Generate a realistic, non-repetitive set of Gerstner waves from a compact set
 * of parameters. Deterministic given a `seed`.
 */
export function generateWaveSpectrum(options: SpectrumOptions = {}): WaveParams[] {
  const {
    count = 24,
    maxWavelength = 13,
    minWavelength = 0.7,
    direction = 0.5,
    spread = 1.0,
    amplitude = 0.6,
    falloff = 1.2,
    speed = 0.9,
    seed = 1,
  } = options;

  const rng = mulberry32(seed);
  const waves: WaveParams[] = [];
  const ratio = minWavelength / maxWavelength;

  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    // Geometric spread from long to short, jittered so ratios stay irrational.
    const jitter = 0.82 + rng() * 0.36;
    const wavelength = Math.max(minWavelength, maxWavelength * ratio ** t * jitter);
    const rel = wavelength / maxWavelength; // 1 for the longest wave, →0 for chop

    // Energy concentrates in the longer swells; short waves are lower.
    const amp = amplitude * rel ** falloff * (0.6 + rng() * 0.4);
    // Shorter waves spread more widely around the dominant direction.
    const dir = direction + (rng() * 2 - 1) * spread * (0.35 + 0.65 * (1 - rel));
    // Deep-water dispersion: phase speed ∝ √wavelength, with a little jitter.
    const phaseSpeed = speed * Math.sqrt(wavelength) * (0.9 + rng() * 0.2);

    waves.push({ direction: dir, wavelength, amplitude: amp, speed: phaseSpeed });
  }
  return waves;
}

/** A ready-made look: a wave set plus the field shaping it was designed with. */
export interface WavePreset {
  waves: WaveParams[];
  /** Crest sharpening (Stokes 2nd harmonic) in [0, 1] applied with this preset. */
  steepness?: number;
}

/**
 * A curated collection of ready-made looks, keyed by display name. Most are
 * built from {@link generateWaveSpectrum}; some are tuned more for beauty than
 * physical accuracy (e.g. crossed swells, or steepness shaping).
 */
export const WAVE_PRESETS: Record<string, WavePreset> = {
  "Glassy dawn": {
    waves: generateWaveSpectrum({
      count: 12,
      maxWavelength: 20,
      minWavelength: 4,
      amplitude: 0.35,
      spread: 0.35,
      direction: 0.6,
      speed: 0.5,
      falloff: 1.6,
      seed: 3,
    }),
  },
  "Calm swell": {
    waves: generateWaveSpectrum({
      count: 16,
      maxWavelength: 16,
      minWavelength: 2,
      amplitude: 0.5,
      spread: 0.5,
      direction: 0.4,
      speed: 0.7,
      falloff: 1.4,
      seed: 7,
    }),
  },
  Lagoon: {
    waves: generateWaveSpectrum({
      count: 20,
      maxWavelength: 9,
      minWavelength: 1.2,
      amplitude: 0.4,
      spread: 0.9,
      direction: 0.3,
      speed: 0.7,
      falloff: 1.3,
      seed: 55,
    }),
  },
  "Open ocean": {
    waves: generateWaveSpectrum({
      count: 28,
      maxWavelength: 13,
      minWavelength: 0.8,
      amplitude: 0.6,
      spread: 1.0,
      direction: 0.5,
      speed: 0.9,
      falloff: 1.2,
      seed: 12,
    }),
  },
  // Two swell trains crossing at ~90° make a woven, diamond-lattice surface.
  "Crossed swells": {
    waves: [
      ...generateWaveSpectrum({
        count: 14,
        maxWavelength: 11,
        minWavelength: 1.5,
        amplitude: 0.5,
        spread: 0.35,
        direction: 0.35,
        speed: 0.9,
        falloff: 1.2,
        seed: 8,
      }),
      ...generateWaveSpectrum({
        count: 14,
        maxWavelength: 10,
        minWavelength: 1.4,
        amplitude: 0.45,
        spread: 0.35,
        direction: 0.35 + Math.PI / 2,
        speed: 0.9,
        falloff: 1.2,
        seed: 9,
      }),
    ],
  },
  "Windy chop": {
    waves: generateWaveSpectrum({
      count: 32,
      maxWavelength: 7,
      minWavelength: 0.4,
      amplitude: 0.5,
      spread: 1.6,
      direction: 1.1,
      speed: 1.1,
      falloff: 0.9,
      seed: 23,
    }),
  },
  "Storm swell": {
    waves: generateWaveSpectrum({
      count: 40,
      maxWavelength: 18,
      minWavelength: 0.5,
      amplitude: 0.9,
      spread: 1.7,
      direction: 0.8,
      speed: 1.2,
      falloff: 1.0,
      seed: 31,
    }),
  },
  // Wind-driven swell: a few coherent trains + strong crest sharpening give
  // sharp ridges with wide, flat troughs between them.
  "Wind swell": {
    waves: generateWaveSpectrum({
      count: 9,
      maxWavelength: 15,
      minWavelength: 3,
      amplitude: 0.7,
      spread: 0.3,
      direction: 0.5,
      speed: 0.9,
      falloff: 1.5,
      seed: 71,
    }),
    steepness: 0.95,
  },
  Ripples: {
    waves: generateWaveSpectrum({
      count: 22,
      maxWavelength: 3,
      minWavelength: 0.25,
      amplitude: 0.3,
      spread: 2.2,
      direction: 0,
      speed: 0.8,
      falloff: 1.0,
      seed: 42,
    }),
  },
};

/** Names of the available presets, in display order. */
export const WAVE_PRESET_NAMES = Object.keys(WAVE_PRESETS);

/** The preset used for {@link DEFAULT_WAVES}. */
export const DEFAULT_PRESET = "Open ocean";
